/**
 * The long-format record sheet of one subject: every section one below the other, each row
 * pointing at the profile path that opens its record, laid out into as many pages as the
 * record needs. Pure, so it is unit tested.
 */
import type { StudyView } from '../dashboards/StudyView.ts';
import { gradeColour } from './layout.ts';

export interface SheetRow {
  cells: string[];
  /** Profile path (after the subject segment) that opens this record. */
  path: string[];
  flag?: boolean;
  /** Per-cell highlight (lab grades). */
  cellColours?: (string | undefined)[];
}

export interface SheetSection {
  title: string;
  header: string[];
  /** Column widths as fractions. */
  widths: number[];
  rows: SheetRow[];
}

/** Visits per lab matrix: more would squeeze the values. */
export const LAB_VISITS_PER_TABLE = 7;

const dash = (v: string | number | undefined | null) =>
  v === undefined || v === null || v === '' ? '—' : String(v);

export function sheetSections(view: StudyView, subjectId: string): SheetSection[] {
  const s = view.subject(subjectId);
  if (!s) return [];
  const file = view.file;
  const order = (a: { visitCode: string }, b: { visitCode: string }) =>
    view.visitIndex(a.visitCode) - view.visitIndex(b.visitCode);
  const sections: SheetSection[] = [];

  sections.push({
    title: 'Demographics',
    header: ['Field', 'Value'],
    widths: [0.38, 0.62],
    rows: [
      ['Site', view.siteLabel(s.siteId)],
      ['Status', `${s.status} (${s.statusDate})`],
      ['Arm', dash(s.arm)],
      ['Sex / age', `${s.sex} / ${s.age}`],
      ['Consent / screening', `${s.consentDate} / ${s.screeningDate}`],
      ['Randomised / first dose', `${dash(s.randomisationDate)} / ${dash(s.firstDoseDate)}`],
      ['Height / weight / BMI', `${s.heightCm} cm / ${s.weightKg} kg / ${s.bmi} (${s.bmiBucket})`],
      [
        'Discontinuation',
        s.discontinuationReason ? `${s.discontinuationReason} (${s.discontinuationDate})` : '—',
      ],
      ['Screen fail reason', dash(s.screenFailReason)],
      ['Flags', s.flags.length ? s.flags.join(', ') : 'none'],
    ].map(([label, value]) => ({
      cells: [label!, value!],
      path: [],
      flag: label === 'Flags' && s.flags.length > 0,
    })),
  });

  const visits = file.visits.filter((v) => v.subjectId === s.id).sort(order);
  sections.push({
    title: 'Visits',
    header: ['Visit', 'Target', 'Actual', 'Timing', 'Pages'],
    widths: [0.26, 0.2, 0.2, 0.17, 0.17],
    rows: visits.map((v) => {
      const pages = file.forms.filter((f) => f.visitId === v.id);
      const entered = pages.filter((f) => f.status !== 'Not Started').length;
      return {
        cells: [
          v.visitName,
          v.targetDate,
          v.actualDate ?? v.status.toLowerCase(),
          v.timing,
          pages.length ? `${entered}/${pages.length}` : '—',
        ],
        path: ['tab:Visits', `visit:${v.id}`],
        flag: v.status === 'Missed' || v.timing === 'Late' || v.timing === 'Early',
      };
    }),
  });

  sections.push({
    title: 'Adverse events',
    header: ['Term', 'Onset', 'Grade', 'Serious', 'Outcome'],
    widths: [0.34, 0.2, 0.12, 0.14, 0.2],
    rows: file.adverseEvents
      .filter((a) => a.subjectId === s.id)
      .sort((a, b) => a.onsetDate.localeCompare(b.onsetDate))
      .map((a) => ({
        cells: [a.term, a.onsetDate, `G${a.grade}`, a.serious ? 'SAE' : 'no', a.outcome],
        path: ['tab:Adverse Events', `ae:${a.id}`],
        flag: a.serious || a.grade >= 3,
      })),
  });

  sections.push({
    title: 'Concomitant medications',
    header: ['Medication', 'Indication', 'Dose', 'Start', 'End'],
    widths: [0.26, 0.26, 0.2, 0.14, 0.14],
    rows: file.conMeds
      .filter((c) => c.subjectId === s.id)
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .map((c) => ({
        cells: [
          c.drug,
          c.indication,
          `${c.dose} ${c.unit} ${c.frequency}`,
          c.startDate,
          c.ongoing ? 'ongoing' : dash(c.endDate),
        ],
        path: ['tab:Con meds', `conmed:${c.id}`],
      })),
  });

  // Every result: one row per analyte, one column per visit with labs, tinted by grade. Long
  // schedules split into several matrices so every value has room.
  const labs = file.labs.filter((l) => l.subjectId === s.id).sort(order);
  const codes = [...new Set(labs.map((l) => l.visitCode))].sort(
    (a, b) => view.visitIndex(a) - view.visitIndex(b),
  );
  const analytes = [...new Set(labs.map((l) => l.analyte))];
  const chunks: string[][] = [];
  for (let i = 0; i < codes.length; i += LAB_VISITS_PER_TABLE)
    chunks.push(codes.slice(i, i + LAB_VISITS_PER_TABLE));
  if (!chunks.length) chunks.push([]);
  for (const chunk of chunks) {
    sections.push({
      title:
        chunks.length > 1
          ? `Laboratory, ${chunk[0]} to ${chunk[chunk.length - 1]}`
          : 'Laboratory (every result)',
      header: ['Analyte', ...chunk],
      widths: [0.3, ...chunk.map(() => 0.7 / Math.max(1, chunk.length))],
      rows: analytes.map((analyte) => {
        const series = labs.filter((l) => l.analyte === analyte);
        const worst = series.reduce((g, l) => Math.max(g, l.grade), 0);
        const at = chunk.map((c) => series.find((l) => l.visitCode === c));
        return {
          cells: [`${analyte} ${series[0]!.unit}`, ...at.map((l) => (l ? String(l.value) : ''))],
          cellColours: [
            undefined,
            ...at.map((l) => (l && l.grade > 0 ? gradeColour(l.grade) : undefined)),
          ],
          path: ['tab:Labs', `analyte:${analyte}`],
          flag: worst >= 2,
        };
      }),
    });
  }

  const vitals = file.vitals.filter((v) => v.subjectId === s.id).sort(order);
  sections.push({
    title: 'Vitals',
    header: ['Visit', 'BP', 'HR', 'Temp', 'Weight'],
    widths: [0.22, 0.22, 0.14, 0.16, 0.26],
    rows: vitals.map((v, i) => {
      const prev = vitals[i - 1];
      const delta = prev ? v.weightKg - prev.weightKg : 0;
      return {
        cells: [
          v.visitCode,
          `${v.systolicBp}/${v.diastolicBp}`,
          String(v.heartRate),
          `${v.temperatureC} °C`,
          `${v.weightKg} kg${prev ? ` (${delta > 0 ? '+' : ''}${delta.toFixed(1)})` : ''}`,
        ],
        path: ['tab:Vitals', `vitals:${v.id}`],
        flag: Math.abs(delta) >= 10,
      };
    }),
  });
  const ecgs = file.ecgs.filter((e) => e.subjectId === s.id).sort(order);
  if (ecgs.length)
    sections.push({
      title: 'ECG',
      header: ['Visit', 'QTcF', 'HR', 'PR / QRS', 'Interpretation'],
      widths: [0.16, 0.16, 0.12, 0.2, 0.36],
      rows: ecgs.map((e) => ({
        cells: [
          e.visitCode,
          `${e.qtcfMs} ms`,
          String(e.heartRate),
          `${e.prMs} / ${e.qrsMs}`,
          e.interpretation,
        ],
        path: ['tab:Vitals', `ecg:${e.id}`],
        flag: e.qtcfMs > 450,
      })),
    });

  sections.push({
    title: 'Protocol deviations',
    header: ['Category', 'Class', 'Date', 'Description'],
    widths: [0.22, 0.14, 0.18, 0.46],
    rows: file.deviations
      .filter((d) => d.subjectId === s.id)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        cells: [d.category, d.classification ?? 'Unclassified', d.date, d.description],
        path: ['tab:Deviations', `deviation:${d.id}`],
        flag: d.classification !== 'Minor',
      })),
  });

  sections.push({
    title: 'Dosing',
    header: ['Visit', 'Date', 'Dose', 'Given', 'Missed'],
    widths: [0.18, 0.24, 0.24, 0.16, 0.18],
    rows: file.dosing
      .filter((d) => d.subjectId === s.id)
      .sort(order)
      .map((d) => ({
        cells: [
          d.visitCode,
          d.date,
          `${d.dose} ${d.unit}`,
          d.administered ? 'yes' : 'no',
          String(d.missedDoses),
        ],
        path: ['tab:Notes', `dosing:${d.id}`],
        flag: !d.administered || d.missedDoses > 2,
      })),
  });

  sections.push({
    title: 'Queries',
    header: ['Raised', 'Form', 'Status', 'Text'],
    widths: [0.18, 0.22, 0.14, 0.46],
    rows: view.queries
      .filter((q) => q.subjectId === s.id)
      .sort((a, b) => b.raisedAt.localeCompare(a.raisedAt))
      .map((q) => ({
        cells: [
          q.raisedAt.slice(0, 10),
          `${q.form}${q.field ? ` · ${q.field}` : ''}`,
          q.status,
          q.text,
        ],
        path: ['tab:Queries', `query:${q.id}`],
        flag: q.status === 'Open',
      })),
  });

  return sections;
}

/* ---------- Pages ---------- */

/** Logical pixel layout of one sheet page. */
export const SHEET_LAYOUT = {
  width: 600,
  height: 1534,
  pad: 18,
  /** Title block height before the subtitle lines, and the height of each subtitle line. */
  titleH: 44,
  subtitleLineH: 18,
  contTitleH: 46,
  sectionH: 30,
  headerSize: 9,
  headerLineH: 12,
  lineH: 15,
  rowPad: 6,
  emptyH: 24,
  gap: 10,
  size: 11,
  /** Space kept free on each side of a cell's text. */
  cellPad: 6,
};

/** Width of `text` at a logical font size and weight (the drawing context's measure). */
export type Measure = (text: string, size: number, weight: number) => number;

export type SheetItem =
  | { kind: 'title'; y: number; h: number; page: number; pages: number }
  | { kind: 'section'; y: number; h: number; section: number; continued: boolean }
  | { kind: 'header'; y: number; h: number; section: number; lines: string[][] }
  | { kind: 'row'; y: number; h: number; section: number; row: number; lines: string[][] }
  | { kind: 'empty'; y: number; h: number; section: number };

export interface SheetLayoutResult {
  pages: SheetItem[][];
  /** Column left edges and widths per section (logical pixels). */
  columns: { x: number; w: number }[][];
}

/** First column is drawn bold; the rest regular. */
export const cellWeight = (column: number) => (column === 0 ? 600 : 500);

/**
 * Column widths: start from the section's fractions, then make every column at least as wide
 * as its widest single word (a date, a value, a code) so nothing has to break mid-word, taking
 * the room from columns that have slack.
 */
export function fitColumns(
  section: Pick<SheetSection, 'widths' | 'rows'>,
  measure: Measure,
  layout = SHEET_LAYOUT,
): { x: number; w: number }[] {
  const inner = layout.width - 2 * layout.pad;
  const need = section.widths.map((_, i) => {
    let widest = 0;
    for (const row of section.rows)
      for (const word of (row.cells[i] ?? '').split(' '))
        widest = Math.max(widest, measure(word, layout.size, cellWeight(i)));
    return widest + layout.cellPad;
  });
  const widths = section.widths.map((f, i) => Math.max(f * inner, need[i]!));
  const excess = widths.reduce((a, b) => a + b, 0) - inner;
  if (excess > 0) {
    const slack = widths.map((w, i) => Math.max(0, w - need[i]!));
    const total = slack.reduce((a, b) => a + b, 0);
    if (total >= excess) widths.forEach((_, i) => (widths[i]! -= (slack[i]! / total) * excess));
    else {
      // Not even the words fit side by side: share the width by need (words will break).
      const sum = need.reduce((a, b) => a + b, 0);
      need.forEach((n, i) => (widths[i] = (n / sum) * inner));
    }
  }
  let x = layout.pad;
  return widths.map((w) => {
    const col = { x, w };
    x += w;
    return col;
  });
}

/**
 * Word wrap that never drops a character: words go on the line while they fit; a word wider
 * than the column breaks, after a hyphen or slash where possible.
 */
export function wrapCell(text: string, width: number, measure: (s: string) => number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (measure(next) <= width) {
      line = next;
      continue;
    }
    if (line) lines.push(line);
    let rest = word;
    while (rest && measure(rest) > width) {
      let cut = rest.length - 1;
      while (cut > 1 && measure(rest.slice(0, cut)) > width) cut--;
      const soft = Math.max(rest.lastIndexOf('-', cut - 1), rest.lastIndexOf('/', cut - 1));
      if (soft > 0) cut = soft + 1;
      lines.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    line = rest;
  }
  if (line || !lines.length) lines.push(line);
  return lines;
}

/**
 * Lays the sections out top to bottom and starts a new page whenever the next row does not
 * fit, repeating the section and column headers on the new page. Nothing is ever cut: every
 * cell and header wraps inside its column, however many lines it takes.
 */
export function paginateSheet(
  sections: SheetSection[],
  measure: Measure,
  layout = SHEET_LAYOUT,
  subtitleLines = 1,
): SheetLayoutResult {
  const pages: SheetItem[][] = [];
  let page: SheetItem[] = [];
  let y = 0;
  const firstTitle = layout.titleH + subtitleLines * layout.subtitleLineH;
  const start = () => {
    page = [];
    const h = pages.length === 0 ? firstTitle : layout.contTitleH;
    page.push({ kind: 'title', y: 0, h, page: pages.length, pages: 0 });
    y = h;
  };
  const bottom = layout.height - layout.pad;
  const fits = (h: number) => y + h <= bottom;
  const columns = sections.map((section) => fitColumns(section, measure, layout));
  start();
  sections.forEach((section, si) => {
    const cols = columns[si]!;
    const width = (i: number) => Math.max(8, cols[i]!.w - layout.cellPad);
    const headerLines = section.header.map((h, i) =>
      wrapCell(h.toUpperCase(), width(i), (t) => measure(t, layout.headerSize, 600)),
    );
    const headerH = Math.max(1, ...headerLines.map((l) => l.length)) * layout.headerLineH + 6;
    const rows = section.rows.map((row) => {
      const lines = row.cells.map((c, i) =>
        wrapCell(c, width(i), (t) => measure(t, layout.size, cellWeight(i))),
      );
      const n = Math.max(1, ...lines.map((l) => l.length));
      return { lines, h: n * layout.lineH + layout.rowPad };
    });
    const heads = (continued: boolean) => {
      page.push({ kind: 'section', y, h: layout.sectionH, section: si, continued });
      y += layout.sectionH;
      page.push({ kind: 'header', y, h: headerH, section: si, lines: headerLines });
      y += headerH;
    };
    const first = rows[0]?.h ?? layout.emptyH;
    if (!fits(layout.sectionH + headerH + first) && page.length > 1) {
      pages.push(page);
      start();
    }
    heads(false);
    if (!rows.length) {
      page.push({ kind: 'empty', y, h: layout.emptyH, section: si });
      y += layout.emptyH;
    }
    rows.forEach((r, ri) => {
      // A row taller than a whole page still goes on a fresh page rather than being cut.
      if (!fits(r.h) && page.some((i) => i.kind === 'row' || i.kind === 'empty')) {
        pages.push(page);
        start();
        heads(true);
      }
      page.push({ kind: 'row', y, h: r.h, section: si, row: ri, lines: r.lines });
      y += r.h;
    });
    y += layout.gap;
  });
  pages.push(page);
  for (const p of pages) for (const item of p) if (item.kind === 'title') item.pages = pages.length;
  return { pages, columns };
}
